import 'intersection-observer';
import { useEffect, useState, cloneElement, useRef, Fragment } from 'react';
import dynamic from 'next/dynamic';
import App from 'next/app';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import UserProvider from 'context/user';
import { ErrorProvider } from 'context/error';
import I18nProvider from 'context/i18n';

import { t, newT, localeRegex } from 'utils/i18n';
import { getTranslations, checkProfile, getI18nTimeStamp, getTranslationsNew } from 'actions/general';
import { autosuggestUnbxd } from 'actions/search';
import { addToBag, getIpadCountries } from 'actions/products';
import { setBagCookies } from 'utils/bag';
import { categories, parseBagItemsEcommerceGA4 } from 'utils/gtmUtils';
import { dataLayerHandleEvent, CAMPAIGN_ID } from 'utils/dataLayers';
import {
  getCookie,
  setCookie,
  COOKIES,
  eraseCookie,
  updateQuerystringParam,
  delayFunction,
  handleStorage,
  setCookieBonds,
  getRealID,
  filterTransformer,
  getAppliedDiscount,
  getRealLanguage,
} from 'utils/helpers';
import { I18N_LABELS } from 'utils/routes';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { GLOBAL_E_SCRIPTS_ID, IS_CAMPER } from 'utils/constants/system';
import { addLazyLoadIntersector, addLazyLoadIntersectorLanding } from 'utils/lazyload';
import { useUtmParams } from 'utils/utm';
import { getDomainValue, isDomain } from 'utils/domain';
import { AppDataProvider } from 'context/app';
import { getNewsletterDefaultData } from 'actions/header';
import { getCountries } from 'actions/global';
import { extractSku } from 'utils/product';
import { getUUID } from 'utils/common';
import { FeatureFlagsProvider } from 'context/feature-flags';
import { useLocale } from 'hooks';

import 'styles/globals.css';
import 'styles/tailwind.css';
import 'styles/autosuggest.css';
import 'styles/antd-custom.less';

const CMSProvider = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.CMSProvider)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.CMSProvider)),
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.CMSProvider)),
});

const ThemeProvider = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.ATOMS.Theme)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.ATOMS.Theme)),
  nnormal: Fragment,
});

const StoresModal = dynamic(() => import('components/storesModal').then((mod) => mod.StoresModal), { ssr: false });
const GreatBritainModal = dynamic(() => import('components/greatBritainModal').then((mod) => mod.GreatBritainModal));

// require('styles/antd-custom.less');

const labelsExecutor = async (labels, callback) => {
  try {
    // cuando el timeout termina, si no hay nuevas, hacemos GET de timestamp
    // aquí en base al TS haremos POST || setearemos el evento de visibilitychange para el POST
    if (labels.length > 0 && navigator?.sendBeacon) {
      const labelsGetResponse = await getI18nTimeStamp(labels);

      if (labelsGetResponse && labelsGetResponse.hash) {
        const { hash, shouldPost } = labelsGetResponse;

        const sendLabels = () => {
          if (process.env.ENVIRONMENT === 'local') return;
          const urlDomain = process.env.MISC_ENDPOINT || (IS_CAMPER ? 'https://misc.camper.com' : 'https://misc.nnormal.com');
          const labelsBlob = new Blob([JSON.stringify(labels)], { type: 'application/json' });
          navigator.sendBeacon(`${urlDomain}${I18N_LABELS}${hash}`, labelsBlob);
          return callback();
        };

        if (shouldPost) {
          return sendLabels();
        }
      }

      return callback();
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

function MyApp({ Component, pageProps, labels, countries, urlBase, profile, newsletterData }) {
  const router = useRouter();
  const { query } = router;
  const { locale, country, language } = useLocale();
  useUtmParams();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 15, // 15 minutes
          },
        },
      }),
  );
  const [appmode, setAppmode] = useState(false);
  const [storeData, setStoreData] = useState(undefined);
  const [autoappliedVoucher, setAutoappliedVoucher] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(undefined);
  const [userInteracted, setUserInteracted] = useState(false);
  const [unbxdInitialSearch, setUnbxdInitialSearch] = useState(null);
  const labelsToSend = useRef([]);
  const sentLabels = useRef([]);
  let sendingLabels = false;
  const { i18nPage } = Component;
  const { sort } = query;

  const changeUserInteraction = (event) => {
    let extraChecks = () => true;

    if (event.type === 'scroll') {
      extraChecks = () => event.currentTarget !== null;
    }

    if (userInteracted === false && extraChecks()) {
      setUserInteracted(true);
      window.removeEventListener('mousemove', changeUserInteraction);
      window.removeEventListener('scroll', changeUserInteraction);
      window.removeEventListener('touchstart', changeUserInteraction);
    }
  };

  useEffect(() => {
    const initAutosuggest = async () => {
      // si estamos en camperlab no hacemos esto
      // if (isDomain('camperlab')) return;
      // buscado general unbxd ya que patterson renderiza 3 searchtnm
      try {
        const isoLanguage = getRealLanguage(language);
        const unbxdUid = getCookie(COOKIES.UNBXD_UID) || null;
        const { buscador } = labels;

        if (buscador) {
          const apiKey = buscador['unbxd.api.key'];
          const siteKey = buscador['unbxd.site.key'];
          const unbxdTranslations = [apiKey, siteKey];

          const results = await autosuggestUnbxd(isoLanguage, country, '*', unbxdTranslations, unbxdUid);

          setUnbxdInitialSearch(results);
        }
      } catch (e) {
        console.error(e);
      }
    };

    // esto estaba en layout pero se necesita en todas las páginas
    const handleLoadIpadCountries = async () => {
      const cookieStoreData = JSON.parse(decodeURIComponent(getCookie(COOKIES.STORE_DATA)));

      if (cookieStoreData) {
        try {
          // por si acaso, que si faltan countries no pierda el storeData
          cookieStoreData.countries = await getIpadCountries({ profile: locale });
        } catch (e) {
          console.error(e);
        }

        setStoreData(cookieStoreData);
      }
    };

    if (typeof window !== 'undefined') {
      try {
        const STORE_MODE = getCookie(COOKIES.STORE_MODE);
        const STORE_DATA = getCookie(COOKIES.STORE_DATA);

        if (i18nPage !== 'StoreIpad' && STORE_MODE === 'true' && !STORE_DATA) {
          window.location.assign(`/${locale}/store_ipad`);
        }
      } catch (e) {
        console.error('[STORE IPAD ERROR] - Failed to redirect to login page', e);
      }
    }

    const countryFromCookie = getCookie(COOKIES.DETECTED_COUNTRY);

    if (process.browser && countryFromCookie !== false && countryFromCookie?.toUpperCase !== undefined) {
      setDetectedCountry(countryFromCookie.toUpperCase());
      window.addEventListener('mousemove', changeUserInteraction);
      window.addEventListener('scroll', changeUserInteraction);
      window.addEventListener('touchstart', changeUserInteraction);
    }
    if (process.browser) {
      window.addToCartMultiple = async (productsArray, isRoku, currentBag) => {
        try {
          let cookiesBagId = getCookie(COOKIES.BAG);
          // const sizeLabel = !isAccessory ? sizes.filter((size) => size?.value === selectedSize)[0]?.label : selectedSize;
          const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
          const voucherBonds = getCookie(COOKIES.BONDS) || '';
          const voucherDiscount = IS_CAMPER && autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
          let lastResult = {};
          const results = [];
          // eslint-disable-next-line no-restricted-syntax
          for (const product of productsArray) {
            const cookiesMarket = getCookie(COOKIES.BAG_MARKET);
            const cookiesShipment = getCookie(COOKIES.SHIPMENT);
            const cookiesBonds = getCookie(COOKIES.BONDS);
            const cookiesZip = getCookie(COOKIES.ZIP);
            const cookiesEmployeeId = getCookie(COOKIES.EMPLOYEE_ID);
            const cookiesPackstation = getCookie(COOKIES.PACKSTATION);
            const cookiesCtr = getCookie(COOKIES.IS_CTR);
            const payload = {
              ...product,
              id: product.sku,
              sizeLabel: product.size,
              profile: { countryId: country, languageId: language },
              thumbnail: `https://cloud.${IS_CAMPER ? 'camper' : 'nnormal'}.com/is/image/JGVzaG9wMDNiYWdncmV5JA==/${product.sku}_L.jpg`,
            };

            if (cookiesMarket !== country) {
              payload.hashOrderId = '';
              if (cookiesBagId) {
                eraseCookie(COOKIES.BAG);
                eraseCookie(COOKIES.BAG_DATE);
              }
              if (cookiesZip) {
                eraseCookie(COOKIES.ZIP);
              }
              if (cookiesShipment) {
                eraseCookie(COOKIES.SHIPMENT);
              }
              if (cookiesMarket !== null && cookiesBonds) {
                eraseCookie(COOKIES.BONDS, COOKIES.DOMAIN);
                eraseCookie(COOKIES.AUTO_BONDS);
              }
              if (cookiesEmployeeId) {
                eraseCookie(COOKIES.EMPLOYEE_ID);
              }
              if (cookiesPackstation) {
                eraseCookie(COOKIES.PACKSTATION);
              }
              if (cookiesCtr) {
                eraseCookie(COOKIES.IS_CTR);
              }
            }
            if (voucherDiscount) {
              payload.voucher = voucherBonds;
            }
            if (cookiesBagId) {
              payload.hashOrderId = decodeURI(cookiesBagId);
            }

            if (isRoku) {
              payload.checkStock = true;
            }
            // eslint-disable-next-line no-await-in-loop
            const result = await addToBag(payload);

            if (['OK', 'NOSTOCK'].includes(result?.status)) {
              lastResult = result;
            }

            if (result.hashOrderId) {
              cookiesBagId = encodeURI(result.hashOrderId);
              setBagCookies({ hash: encodeURI(result.hashOrderId), market: country });
            } else {
              console.error('ERROR addToCartMultiple');
              console.error('Payload: ', payload);
            }

            results.push({ ...product, ...result });
          }

          if (results.filter((item) => item.status === 'OK')?.length > 0) {
            // solo hacemos un addToCartEvent si se ha añadido algo
            // con el control de roku es posible que hayamos intentado
            // añadir varios y no tener éxito con ninguno
            const addedItems = results
              .filter((item) => item.status === 'OK')
              .map((rs) => {
                const item = rs.shoppingCart[0];
                const { name, productId, price: prices, category, target, color, size } = item;
                const currentPrice = prices === null ? 0 : prices?.current;
                const currentPriceEur = prices === null ? 0 : prices?.currentEur;
                const productCategory = category?.split('/');
                const currentList = handleStorage('getItem', 'gtmCurrentList');
                const currentListSplit = currentList ? currentList.split('|') : '';
                const currentListId = currentList && Array.isArray(currentListSplit) ? currentListSplit[0] : '';
                const currentListName = currentList && Array.isArray(currentListSplit) && currentList.length > 1 ? currentListSplit[1] : '';
                const currentListUrl = handleStorage('getItem', 'gtmCurrentListUrl');
                const lastVisitedUrl = document?.referrer ? document?.referrer.split('?')[0] : null;
                const gtmIndex = handleStorage('getItem', 'gtmCurrentItemIndex');
                const itemListName = currentListUrl === lastVisitedUrl ? currentListName : '';
                const itemListId = currentListUrl === lastVisitedUrl && currentList ? currentListId.replaceAll(' ', '_') : '';

                return {
                  item_name: name?.toLowerCase() || '',
                  item_id: productId,
                  price: currentPrice,
                  price_eur: currentPriceEur,
                  price_net: prices?.netPrice ?? 0,
                  discount: getAppliedDiscount(prices),
                  discount_eur: prices?.discountEur || 0,
                  item_brand: IS_CAMPER ? 'camper' : 'nnormal',
                  item_category: target in categories ? categories[target] : '', // 'women
                  item_category2: productCategory.length >= 2 ? productCategory[1].toLowerCase() : '', // shoes
                  item_category3: productCategory.length >= 2 ? productCategory[2].toLowerCase() : '', // sandals
                  item_category4: productCategory.length >= 3 ? productCategory[3].toLowerCase() : '', // <linea>
                  item_category5: size || '', // productCategory && Array.isArray(productCategory) ? productCategory.pop() : '', // DISCOUNT-....
                  item_variant: color?.toLowerCase() || '',
                  item_list_name: itemListName || null,
                  item_list_id: itemListId?.toLowerCase(),
                  item_size: size || '',
                  quantity: 1,
                  index: gtmIndex ? Number(gtmIndex) : 1,
                };
              });

            const fullBag = currentBag?.length > 0 ? [...parseBagItemsEcommerceGA4(currentBag), ...addedItems] : [...addedItems];
            const addToCartEvent = {
              event: 'add_to_cart',
              id_bag: cookiesBagId,
              ecommerce: {
                currencyCode: profile?.currencySap || 'EUR',
                currency: profile?.currencySap || 'EUR',
                value: results
                  .filter((item) => item.status === 'OK')
                  .map((item) => item.price)
                  .reduce((acc, item) => item + acc, 0),
                items: addedItems,
              },
              cart: { items: fullBag },
            };
            dataLayerHandleEvent(addToCartEvent);
          }
          const { sapActive, extendedBlockSAP } = lastResult;
          if (!sapActive && !extendedBlockSAP) {
            // can't pay
            window.location.assign(`${window.location.origin}/${locale}/pedido_offline`);
            return false;
          }
          if (extendedBlockSAP) {
            // can pay
            setCookie(COOKIES.SHIPMENT, '01', undefined, COOKIES.DOMAIN);
          }

          const okResults = results.filter((item) => item.status === 'OK');

          if (!isRoku) {
            window.location.assign(`${window.location.origin}/${locale}/bolsa`);
            return true;
          }

          return [okResults.length === productsArray.length, results];
        } catch (error) {
          console.error('Unexpected ERROR addToCartMultiple');
          return false;
        }
      };
    }

    try {
      const clientCookie = getCookie(COOKIES.CLIENT);

      if (!clientCookie) {
        const newID = getUUID();
        setCookie(COOKIES.CLIENT, newID, null);
      }
    } catch (error) {
      console.error(`Error recuperando / seteando la cookie ${COOKIES.CLIENT}`);
      console.error(error);
    }

    // Cookies de IDIOMA
    if (profile && !profile.global_e) {
      eraseCookie(COOKIES.GLOBAL_E);
    }

    if (locale !== undefined && locale.length === 5 && localeRegex.exec(locale)) {
      setCookie(COOKIES.COUNTRY, country, 30, COOKIES.DOMAIN);
      setCookie(COOKIES.LANG, language, 30, COOKIES.DOMAIN);
    }

    if (query) {
      const { appIpad, workstation, store_id, user_id, voucher, prevoucher } = query;

      const cookiesToSet = { [COOKIES.WORKSTATION]: workstation, [COOKIES.TPV_STORE_ID]: store_id, [COOKIES.TPV_EMPLOYEE_ID]: user_id };

      if (appIpad === 'true') {
        setCookie(COOKIES.APP_IPAD, 'true', undefined);
      } else if (appIpad === 'false') {
        setCookie(COOKIES.APP_IPAD, 'false', undefined); // el OR en setCookie hace que no podamos pasar false directamente
      }

      Object.keys(cookiesToSet).forEach((cookieKey) => {
        const queryValue = cookiesToSet[cookieKey];

        if (queryValue) {
          setCookie(cookieKey, queryValue, undefined, COOKIES.DOMAIN);
        }
      });

      if (voucher) {
        setCookieBonds(voucher);
        setCookie(COOKIES.AUTO_BONDS, '1', COOKIES.EXPIRATION_24H, COOKIES.DOMAIN);
        setAutoappliedVoucher(true);
      }

      if (prevoucher) {
        setCookie(COOKIES.PREVOUCHER, prevoucher, COOKIES.EXPIRATION_24H, COOKIES.DOMAIN);
      }
    }

    const appCookie = getCookie(COOKIES.APP_IPAD);

    if (appCookie === 'false' || query?.appIpad === 'false') {
      setAppmode(false);
      updateQuerystringParam(window.location.search, 'appIpad', false, false);
    }

    if (appCookie === 'true' || query?.appIpad === 'true') {
      setAppmode(true);
      updateQuerystringParam(window.location.search, 'appIpad', true, true);
    }

    handleLoadIpadCountries();
    if (isDomain('camper', 'nnormal')) {
      initAutosuggest();
    }

    return () => {
      window.removeEventListener('mousemove', changeUserInteraction);
      window.removeEventListener('scroll', changeUserInteraction);
      window.removeEventListener('touchstart', changeUserInteraction);
    };
  }, []);

  const sendAndEmptyLabels = () => {
    labelsExecutor([...labelsToSend.current], () => {
      sendingLabels = false;
      labelsToSend.current = [];
      document.removeEventListener('visibilitychange', sendAndEmptyLabels);
    });
  };

  const prepareAndSendI18n = (parent, anchor, sendLocale) => {
    if (process.browser) {
      const i18nString = `${i18nPage}${sendLocale ? ` ${locale} ` : ' '}${parent} ${anchor}`;
      if (!sentLabels.current.includes(i18nString)) {
        labelsToSend.current.push(i18nString);
        sentLabels.current.push(i18nString);
        labelsToSend.current.sort();

        delayFunction(() => {
          // esperamos a que se hayan rellenado
          // como tenemos que llamarlo aquí
          // hacemos el check de sending para solo mandar labels una vez

          if (labelsToSend.current.length !== 0 && sendingLabels === false) {
            sendingLabels = true;
            sendAndEmptyLabels();
            document.addEventListener('visibilitychange', sendAndEmptyLabels);
          }
        }, 10000);
      }
    }
  };

  /**
   * @function wrappedT
   * @param {string} parent
   * @param {string} anchor
   * @param {string} defaultValue
   * @returns
   */
  const wrappedT = (parent, anchor, defaultValue) => {
    // preparamos para registrar labels
    // las mandaremos a un nuevo endpoint
    if (i18nPage) {
      const translationResult = newT(labels, parent, anchor);
      if (translationResult.foundKey !== null) {
        return translationResult.foundKey;
      }

      // de momento lo mandamos solo cuando foundKey es null para el nuevo
      prepareAndSendI18n(parent, anchor, false);
      return translationResult.defaultReturn;
    }

    // en el futuro también tendremos el modo de siempre en el que acumulamos y mandamos todas
    // prepareAndSendI18n(parent, anchor, true);

    return t(locale, labels, parent, anchor, defaultValue);
  };

  useEffect(() => {
    const tpvStore = getCookie(COOKIES.TPV_STORE_ID);

    if (tpvStore && appmode) {
      setCookie(COOKIES.STORE, tpvStore, 30);
    }
  }, [appmode]);

  const additionalGlobalScripts = [];

  const handleTrackables = () => {
    const trackablesTypesData = {};
    const eventItems = [];
    const lazyTrackables = [].slice.call(document.querySelectorAll(`[data-tracking*="${CAMPAIGN_ID}"]:not(.tnm__link)`));

    lazyTrackables.forEach((trackable) => {
      const trackedTargetData = trackable?.dataset?.tracking?.split('|');

      if (trackedTargetData?.length > 3) {
        const promotionId = trackedTargetData[2];
        const trackablesTypeList = [].slice.call(document.querySelectorAll(`[data-tracking*="|${trackedTargetData[3]}"]:not(.tnm__link)`));

        trackablesTypeList.forEach((item) => {
          const trackableData = item.dataset.tracking.split('|');
          const [, , id, creativeType] = trackableData;

          if (id && creativeType) {
            if (Object.keys(trackablesTypesData).includes(creativeType)) {
              if (!trackablesTypesData[creativeType].includes(id)) {
                trackablesTypesData[creativeType].push(id);
              }
            } else {
              trackablesTypesData[creativeType] = [id];
            }
          }
        });

        const alreadyInItems = eventItems.filter((item) => item.promotion_id === promotionId).length > 0;
        const gtmType = i18nPage || '';
        window.gtmType = gtmType;

        if (!alreadyInItems) {
          const creativeTypeList = trackablesTypesData[trackedTargetData[3]];

          if (creativeTypeList === undefined) {
            console.error('Bad tracking:', trackedTargetData);
          } else {
            let creativeSlot = creativeTypeList.indexOf(promotionId);

            if (creativeSlot !== -1) {
              creativeSlot = creativeSlot + 1;
            }

            eventItems.push({
              location_id: gtmType,
              promotion_id: promotionId,
              promotion_name: trackedTargetData[2],
              creative_name: trackedTargetData[3],
              creative_slot: creativeSlot,
              index: eventItems.length + 1,
            });
          }
        }
      }
    });

    if (eventItems.length > 0) {
      const eventGA4 = { event: 'view_promotion', ecommerce: { items: eventItems } };

      handleStorage('setItem', 'gtmPromotionItems', JSON.stringify(eventItems));

      dataLayerHandleEvent(eventGA4);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', (event) => {
        try {
          if (event?.data) {
            const { ready, origin, type, payload } = event?.data || {};

            if (origin === 'roku' && ready === true) {
              handleTrackables();
            } else if (type === 'datalayerevent' && payload !== undefined) {
              dataLayerHandleEvent(payload);
            }
          }
        } catch (e) {
          console.error(e);
        }
      });
    }

    handleTrackables();
  }, []);

  const noIndex = Boolean(sort && i18nPage === 'grid');

  return (
    // controlar provider user solo páginas que requieran
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes, viewport-fit=cover" />
        <script id="lazyloadvideo-intersector">
          {process.browser && document.addEventListener('DOMContentLoaded', addLazyLoadIntersector(pageProps))}
          {process.browser && i18nPage === 'Landing' && document.addEventListener('DOMContentLoaded', addLazyLoadIntersectorLanding(pageProps))}
        </script>
        {additionalGlobalScripts.map((item) =>
          cloneElement(item, { id: `script-${item.key}`, name: `script-${item.key}`, async: item.props?.async || false, defer: item.props?.defer || false }),
        )}
        {profile.global_e === true ?
          <>
            <script
              key="global-e-external"
              type="text/javascript"
              id="GlobalEScript"
              src={
                process.env.ENVIRONMENT === 'prod' ?
                  `https://gepi.global-e.com/includes/js/${GLOBAL_E_SCRIPTS_ID}`
                : `https://intgepi.bglobale.com/includes/js/${GLOBAL_E_SCRIPTS_ID}`
              }
              defer
            />
            <link
              id="GEPIStyles"
              key="global-e-css"
              rel="stylesheet"
              href={
                process.env.ENVIRONMENT === 'prod' ?
                  `https://gepi.global-e.com/includes/css/${GLOBAL_E_SCRIPTS_ID}?countryCode=${country}`
                : `https://intgepi.bglobale.com/includes/css/${GLOBAL_E_SCRIPTS_ID}?countryCode=${country}`
              }
            />
          </>
        : null}
        <meta key="og:price:currency" property="og:price:currency" content={profile.currencySap} />

        {noIndex && <meta name="robots" content="noindex, follow" />}
      </Head>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <CMSProvider language={language} country={country} urlBase={urlBase}>
            <ThemeProvider>
              <FeatureFlagsProvider initialConfig={profile.config}>
                <AppDataProvider newsletter={newsletterData}>
                  <UserProvider i18nPage={i18nPage} autoappliedVoucher={autoappliedVoucher}>
                    <I18nProvider
                      t={wrappedT}
                      appmode={appmode}
                      storeData={storeData}
                      setStoreData={setStoreData}
                      countries={countries}
                      locale={locale}
                      profileData={profile}
                      detectedCountry={detectedCountry} // temporal cambio getHeader en cliente
                      userInteracted={userInteracted}
                      i18nPage={i18nPage}
                      unbxdInitialSearch={unbxdInitialSearch}
                    >
                      <ErrorProvider error={pageProps?.error || false}>
                        <Component {...pageProps} autoappliedVoucher={autoappliedVoucher} />
                        {getDomainValue({ camper: <GreatBritainModal />, nnormal: <StoresModal />, camperlab: null })}
                      </ErrorProvider>
                    </I18nProvider>
                  </UserProvider>
                </AppDataProvider>
              </FeatureFlagsProvider>
            </ThemeProvider>
          </CMSProvider>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </HydrationBoundary>
      </QueryClientProvider>
    </>
  );
}

async function handleGetTranslations({ i18nPage, context, profile, labels }) {
  try {
    if (i18nPage) {
      const newLablesReturn = await getTranslationsNew({ context, profile, page: i18nPage });

      return newLablesReturn;
    } else {
      console.error(`No i18nPage for this page`);
      let labelsSet = new Set([]);

      // añadimos generico para que en caso de no tener labels pueda mostrar el texto
      labelsSet = new Set([...labelsSet, 'generico']);
      if (labels) {
        labelsSet = new Set([...labelsSet, ...labels]);
      }
      const lablesReturn = await getTranslations({ context, profile, relParents: [...labelsSet] });
      return lablesReturn;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
}

async function handleGetProfile({ context, locale, extraProfileParams = {} }) {
  try {
    const profileData = await checkProfile({ context, profile: ['default', 'int', undefined].includes(locale) ? 'en_ES' : locale, ...extraProfileParams });
    return profileData;
  } catch (error) {
    console.error(error);
    return null;
  }
}

MyApp.getInitialProps = async (appContext) => {
  const { Component, router, ctx } = appContext;
  const { req, res } = ctx;
  const { locale } = router;
  const { labels, i18nPage } = Component;
  let environment = 'production';
  let urlBase = IS_CAMPER ? 'https://camper.com' : 'https://www.nnormal.com/';

  try {
    environment = process.env.ENVIRONMENT;
    urlBase = process.env.INTEGRA_DOMAIN;
  } catch (error) {
    console.info('No se ha podido cargar el process.env, usando valores por defecto...');
  }

  const appProps = await App.getInitialProps(appContext);
  const returnValue = { ...appProps, urlBase };

  let extraProfileParams = {};

  if (i18nPage === 'grid') {
    const searchParams = router.query;
    const filters = {};
    const { target, product, brand } = searchParams;

    if (brand !== undefined) {
      filters['filter.lineCollection'] = brand.toUpperCase();
    }

    Object.keys(searchParams).forEach((searchKey) => {
      if (searchKey.startsWith('filter.')) {
        filters[searchKey] = searchParams[searchKey];
      }
    });

    const targetId = getRealID('target', target);
    const productId = getRealID('product', product);
    const encodedFilters = filterTransformer.encode(filters);

    extraProfileParams = { targetId, family: productId, page: 'PLP', filters: encodedFilters };
  }

  if (i18nPage === 'ProductSheet') {
    const searchParams = router.query;
    const realTargetId = getRealID('target', searchParams.target);

    const productCode = extractSku(searchParams?.code);

    extraProfileParams = { gender: realTargetId, page: 'PDP', material: productCode ?? searchParams?.code ?? '' };
  }

  const [labelsResult, countriesResult, profileData, newsletterData] = await Promise.all([
    handleGetTranslations({ i18nPage, context: ctx, profile: locale, labels }),
    getCountries({ locale }),
    handleGetProfile({ context: ctx, locale, extraProfileParams }),
    getNewsletterDefaultData({ locale }),
  ]);
  if (labelsResult) {
    returnValue.labels = labelsResult;
  }
  if (countriesResult) {
    returnValue.countries = countriesResult.countries;
  }
  if (profileData) {
    returnValue.profile = profileData;
  }
  if (newsletterData) {
    returnValue.newsletterData = newsletterData;
  }

  if (['default', 'int'].includes(locale)) {
    if (ctx && ctx.res) {
      const urlSplitted = req.url.split('/');
      const pathname = urlSplitted.splice(2, urlSplitted.length);
      const redirectUrl = `/${profileData.defaultProfile}/${pathname.join('/')}`;

      if (environment !== 'local' && res && res.writeHead && typeof res.writeHead === 'function') {
        // para evitar el error de WRITE AFTER END NEXT DEV
        const encodedRedirectUrl = encodeURI(redirectUrl);
        res.writeHead(307, { Location: encodedRedirectUrl });
        res.end();
      }
    }
  }

  return returnValue;
};

// MyApp.propTypes = {
//   Component: any,
//   pageProps: object,
//   labels: oneOfType([array, object]),
//   locale: string,
//   countries: array,
//   urlBase: string,
//   environment: string,
//   profile: object,
//   storeDataProp: object,
//   unbxdInitialSearch: object,
// };

export default MyApp;
